import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/article/usfans-buying-guide", label: t("nav.buyingGuide") },
    { to: "/usfans-spreadsheet-guide", label: t("nav.guide") },
    { to: "/#categories", label: t("nav.categories") },
    { to: "/#faq", label: t("nav.faq") },
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50 border-b border-background-200">
      <div className="mx-auto flex w-full items-center justify-between gap-4 px-4 py-3 md:px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label={t("brand.name")}>
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-links-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            {t("brand.name")}
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 hover:text-foreground-950"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("nav.explore")}
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 bg-background-50 text-foreground-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-[61px] bottom-0 z-50 flex flex-col bg-background-50 lg:hidden">
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-8 pt-2">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">
              {t("nav.categories")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
                >
                  <img src={c.image} alt={t(`cat.${c.key}`)} className="h-6 w-6 rounded object-cover" />
                  <span className="truncate">{t(`cat.${c.key}`)}</span>
                </a>
              ))}
            </div>

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-4 flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("nav.explore")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}